import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useDarkMode } from '../contexts/DarkModeContext';
import {
  X,
  Crown,
  AlertCircle,
  CheckCircle,
  Upload,
  CreditCard,
  User,
  ArrowRight,
  ArrowLeft,
  MessageSquare,
  Tag,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const RankPurchaseModal = ({ rank, onClose }) => {
  const { isDarkMode } = useDarkMode();
  const [step, setStep] = useState(1);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [ticketId, setTicketId] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [finalPrice, setFinalPrice] = useState(rank.price);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [lastPurchaseTime, setLastPurchaseTime] = useState(null);

  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const gameUsername = watch('gameUsername');

  const DEST_CARD_NUMBER = '6219861923149156';
  const DEST_CARD_HOLDER = 'رفیعی منش';

  const handleCopyCardNumber = async () => {
    try {
      await navigator.clipboard.writeText(DEST_CARD_NUMBER);
      toast.success('شماره کارت کپی شد');
    } catch {
      toast.error('کپی ناموفق بود');
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return toast.error('کد تخفیف را وارد کنید');
    setIsValidatingDiscount(true);
    try {
      const response = await axios.post('/api/discount-codes/validate', {
        code: discountCode,
        amount: rank.price,
        rankType: rank.type
      });
      if (response.data.valid) {
        setDiscountValidation(response.data);
        setDiscountAmount(response.data.discountAmount);
        setFinalPrice(response.data.finalAmount);
        toast.success('کد تخفیف با موفقیت اعمال شد!');
      } else {
        setDiscountValidation({ valid: false, message: response.data.message });
        toast.error(response.data.message || 'کد نامعتبر است');
      }
    } catch (error) {
      toast.error('خطا در بررسی کد تخفیف');
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  const handleNextStep = () => {
    if (step === 2 && !acceptedTerms) {
      toast.error('لطفاً قوانین را مطالعه و تأیید کنید');
      return;
    }
    if (step < 5) setStep(prev => prev + 1);
  };

  const handlePrevStep = () => setStep(prev => prev > 1 ? prev - 1 : 1);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('حجم فایل بیش از ۵ مگابایت است');
    if (!file.type.startsWith('image/')) return toast.error('فقط فایل تصویری مجاز است');
    setReceiptFile(file);
    toast.success('رسید با موفقیت انتخاب شد');
  };

  const createTicket = async (data) => {
    if (!receiptFile) return toast.error('لطفاً رسید پرداخت را آپلود کنید');

    const now = Date.now();
    if (lastPurchaseTime && now - lastPurchaseTime < 5 * 60 * 1000) {
      const remaining = Math.ceil((300000 - (now - lastPurchaseTime)) / 1000);
      return toast.error(`لطفاً ${remaining} ثانیه صبر کنید`);
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error('برای خرید رنک ابتدا وارد حساب کاربری خود شوید');
        window.location.href = '/login';
        return;
      }

      const payload = {
        rankType: rank.type,
        gameUsername: data.gameUsername,
        paymentMethod: 'card',
      };
      if (discountValidation?.valid) payload.discountCode = discountCode;

      const res = await axios.post('/api/rank/purchase', payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.data?.success) throw new Error(res.data?.message);

      const purchaseId = res.data.purchaseId;

      if (receiptFile) {
        const fd = new FormData();
        fd.append('receipt', receiptFile);

        await axios.post(`/api/rank/upload-receipt/${purchaseId}`, fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
      }

      setTicketId(purchaseId);
      setLastPurchaseTime(Date.now());
      setStep(5);
      toast.success('تیکت با موفقیت ایجاد شد!');
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'خطا در ایجاد تیکت';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('fa-IR').format(price);

  const modalVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
  const stepVariants = { hidden: { opacity: 0, x: 30 }, visible: { opacity: 1, x: 0 } };

  const renderStepIndicator = () => (
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${step >= i ? 'bg-emerald-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}>
                  {i}
                </div>
                {i < 5 && <div className={`w-10 h-0.5 ${step > i ? 'bg-emerald-600' : 'bg-gray-200 dark:bg-gray-700'}`} />}
              </div>
          ))}
        </div>
      </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
            <motion.div variants={stepVariants} initial="hidden" animate="visible" className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">نام کاربری در سرور</h3>
                <p className="text-gray-500 dark:text-gray-400">نام کاربری فردی که میخواهید رنک برای او تنظیم شود</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">نام کاربری *</label>
                <input
                    type="text"
                    {...register('gameUsername', {
                      required: 'نام کاربری الزامی است',
                      minLength: { value: 1, message: 'حداقل ۱ کاراکتر' },
                      pattern: { value: /^[a-zA-Z0-9_]+$/, message: 'فقط حروف انگلیسی، عدد و _' }
                    })}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.gameUsername ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                    placeholder="مثال: Player123"
                />
                {errors.gameUsername && <p className="text-red-500 text-sm mt-1">{errors.gameUsername.message}</p>}
              </div>

              <button onClick={handleNextStep} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
                ادامه <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>
        );

      case 2:
        return (
            <motion.div variants={stepVariants} initial="hidden" animate="visible" className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-red-600 dark:text-red-500 mb-2">قوانین خرید رنک</h3>
              </div>

              <div className="bg-red-50 dark:bg-red-950/30 rounded-xl p-5 space-y-3">
                <p className="text-red-700 dark:text-red-400">• در صورت تلپورت به دیگران رنک شما وارن میگیرد</p>
                <p className="text-red-700 dark:text-red-400">• در صورت گرفتن ۳ تا وارن رنک از شما گرفته می شود</p>
                <p className="text-red-700 dark:text-red-400">• پول واریزی به هیچ عنوان برگشت داده نمیشه</p>
                <p className="text-red-700 dark:text-red-400">• در صورت استفاده از کارت دیگران بدون اجازه مجموعه اوالون امسی هیچ مسیولیتی را نمی پذیرد</p>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-gray-700 dark:text-gray-300">تمام قوانین بالا را خوانده ام و می پذیرم</span>
              </label>

              <div className="flex gap-3">
                <button onClick={handlePrevStep} className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
                  <ArrowLeft className="w-5 h-5" /> قبلی
                </button>
                <button
                    onClick={handleNextStep}
                    disabled={!acceptedTerms}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-semibold disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  ادامه <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
        );

      case 3:
        return (
            <motion.div variants={stepVariants} initial="hidden" animate="visible" className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Tag className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">کد تخفیف</h3>
                <p className="text-gray-500 dark:text-gray-400">اختیاری</p>
              </div>

              <div className="flex gap-3">
                <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500 transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="مثال: AVALON50"
                />
                <button
                    onClick={validateDiscountCode}
                    disabled={isValidatingDiscount || !discountCode.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-semibold disabled:opacity-50 transition-all"
                >
                  {isValidatingDiscount ? '...' : 'اعمال'}
                </button>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 space-y-3">
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>قیمت اصلی:</span>
                  <span className="font-semibold">{formatPrice(rank.price)} تومان</span>
                </div>
                {discountValidation?.valid && (
                    <>
                      <div className="flex justify-between text-green-600">
                        <span>تخفیف:</span>
                        <span>-{formatPrice(discountAmount)} تومان</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-200 dark:border-gray-700">
                        <span className="text-gray-800 dark:text-white">مبلغ نهایی:</span>
                        <span className="text-emerald-600">{formatPrice(finalPrice)} تومان</span>
                      </div>
                    </>
                )}
              </div>

              <div className="flex gap-3">
                <button onClick={handlePrevStep} className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
                  <ArrowLeft className="w-5 h-5" /> قبلی
                </button>
                <button onClick={handleNextStep} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
                  ادامه <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
        );

      case 4:
        return (
            <motion.div variants={stepVariants} initial="hidden" animate="visible" className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">پرداخت کارت به کارت</h3>
              </div>

              <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-green-600">{formatPrice(finalPrice)} <span className="text-lg">تومان</span></p>
                {discountValidation?.valid && <p className="text-sm text-green-600 mt-1">شامل {formatPrice(discountAmount)} تومان تخفیف</p>}
              </div>

              <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-5">
                <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-3">شماره کارت مقصد</p>
                <div className="flex items-center justify-between gap-3">
                  <code className="font-mono text-xl tracking-wider text-gray-800 dark:text-white select-all">{DEST_CARD_NUMBER}</code>
                  <button onClick={handleCopyCardNumber} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">کپی</button>
                </div>
                <p className="text-center text-amber-700 dark:text-amber-300 text-sm mt-3">{DEST_CARD_HOLDER}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">آپلود رسید پرداخت *</label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center hover:border-emerald-500 transition-all">
                  <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="receipt" />
                  <label htmlFor="receipt" className="cursor-pointer block">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">{receiptFile ? receiptFile.name : 'کلیک کنید و رسید را آپلود کنید'}</p>
                    <p className="text-xs text-gray-400 mt-1">فقط تصویر • حداکثر ۵ مگابایت</p>
                  </label>
                </div>
                {receiptFile && <p className="text-green-600 text-sm mt-2 flex items-center justify-center gap-1"><CheckCircle className="w-4 h-4" /> رسید آماده ارسال است</p>}
              </div>

              <div className="flex gap-3">
                <button onClick={handlePrevStep} className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
                  <ArrowLeft className="w-5 h-5" /> قبلی
                </button>
                <button
                    onClick={handleSubmit(createTicket)}
                    disabled={isLoading || !receiptFile}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-semibold disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {isLoading ? 'در حال ارسال...' : 'ارسال تیکت'}
                </button>
              </div>
            </motion.div>
        );

      case 5:
        return (
            <motion.div variants={stepVariants} initial="hidden" animate="visible" className="space-y-6 text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white">تیکت خرید ثبت شد!</h3>
              <p className="text-gray-500 dark:text-gray-400">ادمین‌ها به زودی بررسی خواهند کرد</p>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 text-right">
                <h4 className="font-bold text-gray-800 dark:text-white mb-3">خلاصه خرید</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">رنک:</span> <span className="font-semibold">{rank.type}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">نام کاربری:</span> <span className="font-semibold">{gameUsername}</span></div>
                  <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700"><span className="text-gray-500">مبلغ پرداختی:</span> <span className="font-bold text-emerald-600">{formatPrice(finalPrice)} تومان</span></div>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-950/30 rounded-xl p-5">
                <MessageSquare className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">شماره تیکت شما</p>
                <p className="text-3xl font-bold text-green-600 font-mono">{ticketId}</p>
                <p className="text-xs text-gray-500 mt-3">در پنل کاربری میتوانید وضعیت خرید را پیگیری کنید</p>
              </div>

              <button onClick={onClose} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-semibold transition-all">بستن</button>
            </motion.div>
        );

      default:
        return null;
    }
  };

  return (
      <AnimatePresence>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
          <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl`}
              onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6 text-emerald-600" />
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">خرید رنک {rank.type}</h2>
                  <p className="text-sm text-gray-500">مبلغ: {formatPrice(finalPrice)} تومان {discountValidation?.valid && '(با تخفیف)'}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {renderStepIndicator()}
              {renderStepContent()}
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
  );
};

export default RankPurchaseModal;